import { motion } from 'framer-motion'
import type { RadarZones } from '@/services/api/teacher'
import { DEFAULT_TIME_RANGE_DAYS } from '@/utils/radarStorage'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import { hasAnyBackdropFilter } from '../utils/supportsGlassmorphism'
import { calculateZoneRadii } from '../canvas/BubbleRenderer'

interface GlassZonesProps {
  zones: RadarZones
  width: number
  height: number
  timeRange?: { days: number; from: string; to: string }
  locale?: string
}

/**
 * Glass Zones Component - Konzept A: Radialer Gradient
 * Renders a single radial gradient from center (red) to outer (gray)
 * Creates a heatmap-like effect with smooth zone transitions
 */

/**
 * Helper: Convert hex color to rgba with opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export function GlassZones({ zones, width, height, timeRange, locale = 'de' }: GlassZonesProps) {
  const animConfig = useAnimationConfig()

  const supportsGlass = hasAnyBackdropFilter()

  const centerX = width / 2
  const centerY = height / 2
  const maxRadius = Math.min(width, height) / 2 - 60

  const maxHours = (timeRange?.days ?? DEFAULT_TIME_RANGE_DAYS) * 24
  const zoneRadii = calculateZoneRadii(zones, maxHours)
  
  const panicOuterRadius = maxRadius * zoneRadii.panicOuter
  const middleOuterRadius = maxRadius * zoneRadii.middleOuter
  const outerOuterRadius = maxRadius * zoneRadii.outerOuter

  const delays = {
    outer: 0.2,
    middle: 0.35,
    panic: 0.5,
  }

  const getTransition = (delay: number) => {
    if (animConfig.reducedMotion) {
      return { duration: 0, delay }
    }
    return {
      type: 'spring' as const,
      stiffness: 400,
      damping: 30,
      delay,
    }
  }

  const outerStrokeColor = hexToRgba(zones.outer_zone.color, 0.4)
  const middleStrokeColor = hexToRgba(zones.middle_zone.color, 0.5)

  const zonesLabel = locale === 'en' ? 'Radar Zones' : 'Radar-Zonen'
  const ariaLabel = `${zonesLabel}: ${zones.panic_zone.label} (≤${zones.panic_zone.threshold_hours ?? 2}h), ${zones.middle_zone.label} (${zones.panic_zone.threshold_hours ?? 2}-${zones.middle_zone.threshold_hours ?? 48}h), ${zones.outer_zone.label} (>${zones.middle_zone.threshold_hours ?? 48}h)`

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ width, height }}
      role="img"
      aria-label={ariaLabel}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="radar-zone-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={hexToRgba(zones.panic_zone.color, 0.25)} />
            <stop offset="15%" stopColor={hexToRgba(zones.panic_zone.color, 0.15)} />
            <stop offset="25%" stopColor={hexToRgba(zones.middle_zone.color, 0.12)} />
            <stop offset="50%" stopColor={hexToRgba(zones.middle_zone.color, 0.08)} />
            <stop offset="60%" stopColor={hexToRgba(zones.outer_zone.color, 0.06)} />
            <stop offset="100%" stopColor={hexToRgba(zones.outer_zone.color, 0.02)} />
          </radialGradient>
        </defs>

        <motion.circle
          cx={centerX}
          cy={centerY}
          r={outerOuterRadius}
          fill="url(#radar-zone-gradient)"
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={getTransition(delays.outer)}
        />

        <motion.circle
          cx={centerX}
          cy={centerY}
          r={outerOuterRadius}
          fill="none"
          stroke={outerStrokeColor}
          strokeWidth={2}
          strokeDasharray="4 3"
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={getTransition(delays.outer)}
        />

        <motion.circle
          cx={centerX}
          cy={centerY}
          r={middleOuterRadius}
          fill="none"
          stroke={middleStrokeColor}
          strokeWidth={2}
          strokeDasharray="4 3"
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={getTransition(delays.middle)}
        />
      </svg>

      <motion.div
        className={`absolute rounded-full ${supportsGlass ? 'glass-panic-zone' : ''}`}
        style={{
          left: centerX - panicOuterRadius,
          top: centerY - panicOuterRadius,
          width: panicOuterRadius * 2,
          height: panicOuterRadius * 2,
          border: '2px solid rgba(239, 68, 68, 0.5)',
          ...(!supportsGlass && {
            background: 'rgba(239, 68, 68, 0.25)',
          }),
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: 1,
          opacity: 1,
          ...(animConfig.reducedMotion
            ? {}
            : {
                boxShadow: [
                  '0 0 40px rgba(239, 68, 68, 0.15)',
                  '0 0 70px rgba(239, 68, 68, 0.35)',
                  '0 0 40px rgba(239, 68, 68, 0.15)',
                ],
              }),
        }}
        transition={{
          scale: getTransition(delays.panic),
          ...(animConfig.reducedMotion
            ? {}
            : {
                boxShadow: {
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }),
        }}
      >
        {!animConfig.reducedMotion && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(
                circle,
                rgba(239, 68, 68, 0.2) 0%,
                transparent 70%
              )`,
            }}
            animate={{
              scale: [1, 1.05, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </motion.div>

      <GlassZoneLabel
        label={zones.panic_zone.label}
        sublabel={`≤${zones.panic_zone.threshold_hours ?? 2}h`}
        x={centerX}
        y={centerY - (panicOuterRadius - 8)}
        color="#ef4444"
        delay={delays.panic + 0.2}
        reducedMotion={animConfig.reducedMotion}
        zIndex={10}
      />

      <GlassZoneLabel
        label={zones.middle_zone.label}
        sublabel={`${zones.panic_zone.threshold_hours ?? 2}-${zones.middle_zone.threshold_hours ?? 48}h`}
        x={centerX}
        y={centerY - middleOuterRadius / 2}
        color="#f97316"
        delay={delays.middle + 0.2}
        reducedMotion={animConfig.reducedMotion}
        zIndex={10}
      />

      <GlassZoneLabel
        label={zones.outer_zone.label}
        sublabel={`>${zones.middle_zone.threshold_hours ?? 48}h`}
        x={centerX}
        y={centerY - (middleOuterRadius + outerOuterRadius) / 2}
        color="#94a3b8"
        delay={delays.outer + 0.2}
        reducedMotion={animConfig.reducedMotion}
        zIndex={10}
      />
    </div>
  )
}

interface GlassZoneLabelProps {
  label: string
  sublabel: string
  x: number
  y: number
  color: string
  delay: number
  reducedMotion: boolean
  zIndex?: number
}

function GlassZoneLabel({ label, sublabel, x, y, color, delay, reducedMotion, zIndex = 10 }: GlassZoneLabelProps) {
  const transition = reducedMotion
    ? { duration: 0, delay }
    : {
        type: 'spring' as const,
        stiffness: 400,
        damping: 30,
        delay,
      }

  return (
    <motion.div
      className="absolute flex flex-col items-center"
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)',
        zIndex,
      }}
      role="text"
      aria-label={`${label}: ${sublabel}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition}
    >
      <span
        className="text-xs font-bold tracking-wider uppercase"
        style={{
          color,
          textShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {label}
      </span>
      <span
        className="text-[10px] font-medium opacity-80"
        style={{
          color,
          textShadow: '0 1px 2px rgba(0,0,0,0.08)',
        }}
      >
        {sublabel}
      </span>
    </motion.div>
  )
}
